import { useLayoutEffect, useRef, useState } from 'react';
import { REACTIONS, REACTION_GROUPS, QUICK_REACTIONS, findReaction, reactionUrl, type MessageDTO } from '@campus/core';
import { recordReactionUse, useMostUsedReactions } from '../../lib/reactionUsage.js';
import { Icon, Menu, MenuItem, MenuSeparator } from '../ds/index.js';
import { useMembersStore, sessionInfo } from '../people/membersStore.js';
import { toggleReaction } from '../../messaging/store.js';
import { useIsMobile } from '../hooks/useIsMobile.js';

/**
 * Réactions et actions d'un message (tasks/messagerie-plan.md, § 7), façon
 * WhatsApp : au survol, seulement une petite flèche dans le coin du message ;
 * un clic ouvre le menu (réactions rapides, « + » pour les 37 emojis du
 * campus par groupes, Répondre, Modifier, Supprimer). Avant, une barre de
 * boutons apparaissait au moindre survol, recouvrait le message du dessus et
 * se cliquait par mégarde. Pastilles « emoji + nombre » sous le message.
 */

/** Menu d'un message. Ouvert vers le haut quand la place manque en dessous (derniers messages du fil). */
export function MessageMenu({ message, canReact, canAct, onReply, onEdit, onDelete, onClose }: {
  message: MessageDTO;
  canReact: boolean;
  canAct: boolean;
  onReply: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const isMobile = useIsMobile();
  const ref = useRef<HTMLDivElement>(null);
  const [all, setAll] = useState(false);
  const [up, setUp] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current?.firstElementChild as HTMLElement | null;
    if (!el || isMobile) return;
    const box = (ref.current!.closest('[data-scroll-area]') ?? document.documentElement).getBoundingClientRect();
    const msg = ref.current!.parentElement!.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    if (r.bottom > box.bottom && msg.top - box.top > box.bottom - msg.bottom) setUp(true);
  }, [all, isMobile]);
  const mostUsed = useMostUsedReactions(QUICK_REACTIONS, QUICK_REACTIONS.length);
  const react = (id: string) => { void toggleReaction(message, id); onClose(); };
  const item = (id: string, size: number) => {
    const r = findReaction(id)!;
    return (
      <button key={id} type="button" className="pl-msg-react" aria-label={`Réagir : ${r.label}`} title={r.label} onClick={() => react(id)}>
        <img src={reactionUrl(id)} alt="" width={size} height={size} draggable={false} />
      </button>
    );
  };
  return (
    <div ref={ref} onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
      <Menu
        label="Actions du message" onClose={onClose} sheet={isMobile} className="pl-msg-menu"
        style={isMobile ? undefined : up ? { position: 'absolute', right: 8, bottom: 'calc(100% - 6px)' } : { position: 'absolute', right: 8, top: 26 }}
      >
        {canReact && (all ? (
          REACTION_GROUPS.map((g) => (
            <div key={g.id}>
              <p className="pl-menu-label" style={{ margin: '0 0 2px', padding: '4px 4px 2px' }}>{g.label}</p>
              <div className="pl-msg-picker-grid">{REACTIONS.filter((r) => r.group === g.id).map((r) => item(r.id, 24))}</div>
            </div>
          ))
        ) : (
          <>
            <div className="pl-msg-quick">
              {QUICK_REACTIONS.map((id) => item(id, 24))}
              <button type="button" className="pl-msg-react" aria-label="Autres réactions" title="Autres réactions" onClick={() => setAll(true)}>
                <Icon name="plus" size={18} />
              </button>
            </div>
            {mostUsed.length > 0 && (
              <div className="pl-msg-quick" aria-label="Tes plus utilisées" title="Tes plus utilisées">
                {mostUsed.map((id) => item(id, 24))}
              </div>
            )}
          </>
        ))}
        {!all && (<>
          {canReact && <MenuSeparator />}
          {canReact && <MenuItem icon="reply" onSelect={() => { onReply(); onClose(); }}>Répondre</MenuItem>}
          {canAct && <MenuItem icon="edit" onSelect={() => { onEdit(); onClose(); }}>Modifier</MenuItem>}
          {canAct && <MenuItem icon="trash" danger onSelect={() => { onDelete(); onClose(); }}>Supprimer</MenuItem>}
        </>)}
      </Menu>
    </div>
  );
}

/** Pastilles sous le message : la sienne surlignée, clic = ajouter/retirer, survol = qui a réagi. */
export function ReactionPills({ message }: { message: MessageDTO }) {
  const members = useMembersStore((s) => s.members);
  const { userId: me } = sessionInfo();
  if (message.reactions.length === 0 || message.deletedAt) return null;
  const nameOf = (userId: string) => (userId === me ? 'Toi'
    : userId.startsWith('guest:') ? 'Invité' : (members.get(userId)?.displayName ?? 'Membre'));
  return (
    <div className="pl-msg-reactions" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
      {message.reactions.map((r) => {
        const info = findReaction(r.reactionId);
        if (!info) return null;
        const mine = !!me && r.userIds.includes(me);
        // « Toi » en premier, puis dans l'ordre d'arrivée.
        const names = [...r.userIds].sort((a, b) => Number(b === me) - Number(a === me)).map(nameOf);
        const who = names.length <= 1 ? names.join('')
          : `${names.slice(0, -1).join(', ')} et ${names[names.length - 1]}`;
        return (
          <button
            key={r.reactionId} type="button" className={`pl-reaction-pill${mine ? ' is-mine' : ''}`} aria-pressed={mine}
            data-tip={`${info.label} : ${who}`}
            aria-label={`${info.label}, ${r.userIds.length} : ${who}. ${mine ? 'Retirer ta réaction' : 'Réagir pareil'}`}
            onClick={() => { void toggleReaction(message, r.reactionId); }}
          >
            <img src={reactionUrl(r.reactionId)} alt="" width={16} height={16} draggable={false} />
            <span>{r.userIds.length}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Sélecteur d'emojis de la barre de saisie : les 37 emojis du campus par
 * groupes (mêmes images que les réactions) ; un clic insère le caractère à
 * l'endroit du curseur et laisse le menu ouvert pour en enchaîner plusieurs.
 */
/** Emojis à insérer dans la saisie, ouverts depuis le « + » de la saisie (posé au-dessus d'elle). */
export function EmojiPanel({ onPick, onClose }: { onPick: (char: string) => void; onClose: () => void }) {
  const isMobile = useIsMobile();
  const mostUsed = useMostUsedReactions([], 8);
  const pick = (id: string, char: string) => { recordReactionUse(id); onPick(char); };
  return (
    <Menu
      label="Emojis" onClose={onClose} sheet={isMobile} className="pl-msg-menu"
      style={isMobile ? undefined : { position: 'absolute', left: 16, bottom: 'calc(100% + 4px)', width: 'min(300px, calc(100% - 32px))', maxHeight: 320, overflowY: 'auto' }}
    >
      <div>
        <p className="pl-menu-label" style={{ margin: '0 0 2px', padding: '4px 4px 2px' }}>Tes plus utilisés</p>
        <div className="pl-msg-picker-grid">
          {mostUsed.map((id) => findReaction(id)!).map((r) => (
            <button key={r.id} type="button" className="pl-msg-react" aria-label={r.label} title={r.label} onClick={() => pick(r.id, r.char)}>
              <img src={reactionUrl(r.id)} alt="" width={24} height={24} draggable={false} />
            </button>
          ))}
        </div>
      </div>
      {REACTION_GROUPS.map((g) => (
        <div key={g.id}>
          <p className="pl-menu-label" style={{ margin: '0 0 2px', padding: '4px 4px 2px' }}>{g.label}</p>
          <div className="pl-msg-picker-grid">
            {REACTIONS.filter((r) => r.group === g.id).map((r) => (
              <button key={r.id} type="button" className="pl-msg-react" aria-label={r.label} title={r.label} onClick={() => pick(r.id, r.char)}>
                <img src={reactionUrl(r.id)} alt="" width={24} height={24} draggable={false} />
              </button>
            ))}
          </div>
        </div>
      ))}
    </Menu>
  );
}
